<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import CommonButton from '@/components/common/button/CommonButton.vue'
import UploadStatusTable from '@/components/propertyTaxUpload/UploadStatusTable.vue'
import { usePartsCombination } from '@/composables/propertyTaxUpload/usePartsCombination'
import FlightProjectSelector from '@/components/propertyTaxUpload/FlightProjectSelector.vue'
import { getAcceptString } from '@/constants/ui/propertyTaxUpload/uploadAccepts'
import { computed, onMounted, ref } from 'vue'
import { usePropertyTaxUploadStore } from '@/stores/propertyTaxUploadStore'

const {
  fileInput,
  isUploading,
  uploadButtonDisabled,
  cancelButtonDisabled,
  isDragOver,
  dragOver,
  dragLeave,
  dropFile,
  onClickArea,
  onFileChange,
  upload,
  cancel
} = usePartsCombination()

const acceptString = getAcceptString()
const uploadStore = usePropertyTaxUploadStore()
const isFetched = ref(false)

onMounted(async () => {
  await uploadStore.fetchGetProjects()
  isFetched.value = true
})

/** プロジェクトが作成されているかどうか */
const hasProjects = computed(() => {
  return uploadStore.flightProjectData.projectList.length > 0
})
</script>

<template>
  <div v-if="isFetched">
    <div v-if="hasProjects">
      <h2 class="title">空撮画像アップローダー(固定資産)</h2>
      <FlightProjectSelector />
      <div
        class="drop-area"
        :class="{ dragover: isDragOver }"
        @dragover.prevent="dragOver"
        @dragleave.prevent="dragLeave"
        @drop.prevent="dropFile"
        @click="onClickArea"
      >
        <div class="drop-area-content">
          <div class="pb-4"><FontAwesomeIcon :icon="['fas', 'upload']" /></div>
          <div>空撮画像を1フライトごとにZip形式に圧縮し</div>
          <div>
            ファイルをここにドラッグ＆ドロップするか、クリックしてファイルを選択してください
          </div>
        </div>
        <input
          ref="fileInput"
          type="file"
          :accept="acceptString"
          multiple
          @change="onFileChange"
          style="display: none"
        />
      </div>
      <div class="d-flex justify-content-center align-items-center pt-4">
        <div>
          <CommonButton
            variant="primary"
            size="lg"
            class="fs-4"
            :disabled="uploadButtonDisabled"
            :loading="isUploading"
            loading-text="アップロード中"
            @click="() => upload()"
          >
            アップロード
          </CommonButton>
        </div>
        <div>
          <CommonButton
            variant="outline-primary"
            size="lg"
            class="fs-4 mx-4 cancel-button"
            :disabled="cancelButtonDisabled"
            @click="() => cancel()"
          >
            キャンセル
          </CommonButton>
        </div>
      </div>
      <div class="pt-4">
        <UploadStatusTable></UploadStatusTable>
      </div>
    </div>
    <div v-else>
      <h1>プロジェクトが未設定のためアップロードできません。自治体管理者にお問合せください。</h1>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.title {
  margin-bottom: 30px;
}
.drop-area {
  width: 100%;
  height: 250px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  transition: background-color 0.3s ease;
  cursor: pointer;

  .drop-area-content {
    font-size: 2rem;
    font-weight: 600;
    text-align: center;
  }
}

.drop-area.dragover {
  background-color: #f0f8ff;
  border-color: #3b82f6;
}

.cancel-button {
  background-color: #ffffff !important;
}
</style>
